import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { apiClient } from "@/services/api";
import type { LoanInterestRateHistory, LoanInterestRateHistoryFormData } from "@/types";
import { formatDisplayDate } from "@/lib/date.utils";
import { toast } from "@/lib/toast";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Textarea } from "@groot/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Plus, Trash2, Pencil, TrendingUp } from "lucide-react";
import { accountKeys } from "@/hooks/api/query-keys";

interface LoanInterestRateHistoryProps {
  accountId: number;
}

export function LoanInterestRateHistoryComponent({ accountId }: LoanInterestRateHistoryProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LoanInterestRateHistory | null>(null);

  const { data: rateHistory = [], isLoading } = useQuery({
    queryKey: accountKeys.interestRateHistory(accountId),
    queryFn: () => apiClient.getInterestRateHistory(accountId),
  });

  const createMutation = useMutation({
    mutationFn: (data: LoanInterestRateHistoryFormData) =>
      apiClient.createInterestRateEntry(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.interestRateHistory(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      setIsDialogOpen(false);
      toast({ title: "Success", description: "Interest rate entry added" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add entry",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LoanInterestRateHistoryFormData> }) =>
      apiClient.updateInterestRateEntry(accountId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.interestRateHistory(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      setIsDialogOpen(false);
      setEditingEntry(null);
      toast({ title: "Success", description: "Interest rate entry updated" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update entry",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteInterestRateEntry(accountId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.interestRateHistory(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      toast({ title: "Success", description: "Interest rate entry deleted" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete entry",
      });
    },
  });

  const handleAddNew = () => {
    setEditingEntry(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (entry: LoanInterestRateHistory) => {
    setEditingEntry(entry);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingEntry(null);
  };

  const currentRate = rateHistory[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <TrendingUp className="size-4" />
            Interest Rate History
          </h3>
          {currentRate && (
            <p className="text-sm text-muted-foreground mt-1">
              Current:{" "}
              <span className="font-medium text-foreground">
                {Number(currentRate.effectiveRate).toFixed(2)}%
              </span>
              {" · "}
              {formatDisplayDate(currentRate.effectiveDate)}
            </p>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={handleAddNew}>
          <Plus className="size-3.5" />
          Add
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
      ) : rateHistory.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
          No rate history. Click "Add" to record your first entry.
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="text-xs">Date</TableHead>
                <TableHead className="text-xs text-right">Rate</TableHead>
                <TableHead className="text-xs text-right">Repo</TableHead>
                <TableHead className="text-xs text-right">RBLR</TableHead>
                <TableHead className="text-xs text-right">Spread</TableHead>
                <TableHead className="text-xs text-right">CRP</TableHead>
                <TableHead className="w-[70px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rateHistory.map((entry, index) => (
                <TableRow key={entry.id} className={index === 0 ? "bg-info/10/50" : ""}>
                  <TableCell className="text-sm">
                    {formatDisplayDate(entry.effectiveDate)}
                    {index === 0 && (
                      <span className="ml-1.5 text-[10px] font-medium text-info uppercase">
                        Current
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right font-medium">
                    {Number(entry.effectiveRate).toFixed(2)}%
                  </TableCell>
                  <TableCell className="text-sm text-right text-muted-foreground">
                    {entry.repoRate != null ? `${Number(entry.repoRate).toFixed(2)}%` : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-right text-muted-foreground">
                    {entry.rblr != null ? `${Number(entry.rblr).toFixed(2)}%` : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-right text-muted-foreground">
                    {entry.spread != null ? `${Number(entry.spread).toFixed(2)}%` : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-right text-muted-foreground">
                    {entry.crp != null ? `${Number(entry.crp).toFixed(2)}%` : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => handleEdit(entry)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm("Delete this entry?")) {
                            deleteMutation.mutate(entry.id);
                          }
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntry ? "Edit Rate" : "New Interest Rate"}</DialogTitle>
            <DialogDescription>
              {editingEntry
                ? "Update this interest rate entry."
                : "Add a new interest rate. Previous values are prefilled."}
            </DialogDescription>
          </DialogHeader>
          <InterestRateForm
            entry={editingEntry}
            previousEntry={editingEntry ? null : currentRate}
            onSubmit={(data) => {
              if (editingEntry) {
                updateMutation.mutate({ id: editingEntry.id, data });
              } else {
                createMutation.mutate(data);
              }
            }}
            onCancel={handleCloseDialog}
            isSubmitting={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface InterestRateFormProps {
  entry: LoanInterestRateHistory | null;
  previousEntry: LoanInterestRateHistory | null | undefined;
  onSubmit: (data: LoanInterestRateHistoryFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

function InterestRateForm({
  entry,
  previousEntry,
  onSubmit,
  onCancel,
  isSubmitting,
}: InterestRateFormProps) {
  // Use entry if editing, otherwise prefill from previousEntry
  const prefillSource = entry || previousEntry;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoanInterestRateHistoryFormData>({
    defaultValues: {
      effectiveDate: entry
        ? entry.effectiveDate.split("T")[0]
        : new Date().toISOString().split("T")[0],
      effectiveRate: prefillSource ? Number(prefillSource.effectiveRate) : undefined,
      repoRate: prefillSource?.repoRate != null ? Number(prefillSource.repoRate) : undefined,
      rblr: prefillSource?.rblr != null ? Number(prefillSource.rblr) : undefined,
      spread: prefillSource?.spread != null ? Number(prefillSource.spread) : undefined,
      crp: prefillSource?.crp != null ? Number(prefillSource.crp) : undefined,
      notes: entry?.notes || "",
    },
  });

  const rblr = watch("rblr");
  const spread = watch("spread");
  const crp = watch("crp");

  const calculateEffectiveRate = () => {
    if (rblr !== undefined && spread !== undefined) {
      const calculated = (rblr || 0) + (spread || 0) + (crp || 0);
      setValue("effectiveRate", Number(calculated.toFixed(2)));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="effectiveDate" className="text-sm">
          Effective Date
        </Label>
        <Input
          id="effectiveDate"
          type="date"
          {...register("effectiveDate", { required: "Required" })}
        />
        {errors.effectiveDate && (
          <p className="text-xs text-destructive">{errors.effectiveDate.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="repoRate" className="text-sm">
            Repo %
          </Label>
          <Input
            id="repoRate"
            type="number"
            step="0.01"
            placeholder="6.50"
            {...register("repoRate", { valueAsNumber: true })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rblr" className="text-sm">
            RBLR %
          </Label>
          <Input
            id="rblr"
            type="number"
            step="0.01"
            placeholder="9.15"
            {...register("rblr", { valueAsNumber: true })}
            onBlur={calculateEffectiveRate}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="spread" className="text-sm">
            Spread %
          </Label>
          <Input
            id="spread"
            type="number"
            step="0.01"
            placeholder="0.35"
            {...register("spread", { valueAsNumber: true })}
            onBlur={calculateEffectiveRate}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="crp" className="text-sm">
            CRP %
          </Label>
          <Input
            id="crp"
            type="number"
            step="0.01"
            placeholder="0.15"
            {...register("crp", { valueAsNumber: true })}
            onBlur={calculateEffectiveRate}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="effectiveRate" className="text-sm">
          Effective Rate %
        </Label>
        <Input
          id="effectiveRate"
          type="number"
          step="0.01"
          placeholder="9.00"
          {...register("effectiveRate", {
            required: "Required",
            valueAsNumber: true,
            min: { value: 0, message: "Must be positive" },
            max: { value: 100, message: "Max 100%" },
          })}
        />
        {errors.effectiveRate && (
          <p className="text-xs text-destructive">{errors.effectiveRate.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="notes" className="text-sm">
          Notes (optional)
        </Label>
        <Textarea
          id="notes"
          rows={2}
          placeholder="e.g., RBI rate revision"
          {...register("notes")}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : entry ? "Update" : "Add"}
        </Button>
      </div>
    </form>
  );
}
